/**
 * FirewallPolicies — catalog kind "firewall_policy". One component drives two nav
 * entries: Firewall > Rules (L3/L4 policy) and Wireless > SSID Firewall & Rate Limits
 * (same policy object, framed per-SSID with an optional rate limit). Canonical
 * org-scoped catalog objects; CRUD over /api/v2/catalog/firewall_policy.
 */
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useCatalog, useSaveCatalogObject, useDeleteCatalogObject, type CatalogObject } from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";

const INPUT = "rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand";

interface FwRule {
  action: "allow" | "deny";
  protocol: "any" | "tcp" | "udp" | "icmp";
  src: string;
  dst: string;
  port: string;
  rate_kbps?: number | null;
}

export default function FirewallPolicies({ shaping = false }: { shaping?: boolean }) {
  const { data, isLoading, error } = useCatalog("firewall_policy");
  const [editing, setEditing] = useState<CatalogObject | null>(null);
  const [creating, setCreating] = useState(false);
  const rows = data?.items ?? [];

  const title = shaping ? "SSID Firewall & Rate Limits" : "Firewall Rules";
  const subtitle = shaping
    ? "Per-SSID L3/L4 policy and bandwidth limits — referenced from wireless SSIDs"
    : "L3/L4 firewall policies — an ordered allow/deny rule set with a default action";

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <PageHeader title={title} subtitle={subtitle} />
        <RoleGate min="org_admin" mode="hide">
          <Button variant="primary" onClick={() => setCreating(true)}>Add policy</Button>
        </RoleGate>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading policies…</div>}

      {data && rows.length === 0 && (
        <EmptyState
          title="No firewall policies yet"
          hint="Create a policy (e.g. Guest-Isolation: default deny, allow DNS/DHCP/HTTPS to the internet only) and attach it to an SSID."
        />
      )}

      {rows.length > 0 && (
        <ul className="flex flex-col gap-2">
          {rows.map((p) => (
            <li key={p.id} className="md-card flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-ink">{p.name}</span>
                  <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-ink-subtle">
                    default {String(p.default_action)}
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-ink-muted">
                  {((p.rules as FwRule[]) ?? []).length} rule(s)
                </div>
              </div>
              <RoleGate min="org_admin" mode="hide">
                <Button variant="secondary" onClick={() => setEditing(p)}>Edit</Button>
              </RoleGate>
            </li>
          ))}
        </ul>
      )}

      {creating && <PolicyDialog shaping={shaping} onClose={() => setCreating(false)} />}
      {editing && <PolicyDialog shaping={shaping} obj={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function PolicyDialog({ obj, shaping, onClose }: { obj?: CatalogObject; shaping: boolean; onClose: () => void }) {
  const save = useSaveCatalogObject("firewall_policy");
  const del = useDeleteCatalogObject("firewall_policy");
  const editing = !!obj;

  const [name, setName] = useState(obj?.name ?? "");
  const [defaultAction, setDefaultAction] = useState<string>((obj?.default_action as string) ?? "allow");
  const [rules, setRules] = useState<FwRule[]>(((obj?.rules as FwRule[]) ?? []).slice());

  const setRule = (i: number, patch: Partial<FwRule>) =>
    setRules((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRule = () =>
    setRules((rs) => [...rs, { action: "allow", protocol: "any", src: "", dst: "", port: "", rate_kbps: null }]);
  const rmRule = (i: number) => setRules((rs) => rs.filter((_, j) => j !== i));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    save.mutate(
      {
        ...(editing ? { id: obj!.id } : {}),
        name: name.trim(),
        default_action: defaultAction,
        rules: rules.map((r) => ({
          action: r.action,
          protocol: r.protocol,
          src: r.src.trim() || "any",
          dst: r.dst.trim() || "any",
          port: r.port.trim() || "any",
          ...(shaping ? { rate_kbps: r.rate_kbps || null } : {}),
        })),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[85vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">
            {editing ? `Edit ${obj!.name}` : "Add firewall policy"}
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Rules match top-down; the first match wins, otherwise the default action applies.
          </Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <div className="grid grid-cols-3 gap-2">
              <label className="col-span-2 flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Guest-Isolation" className={INPUT} />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Default action</span>
                <select value={defaultAction} onChange={(e) => setDefaultAction(e.target.value)} className={INPUT}>
                  <option value="allow">allow</option>
                  <option value="deny">deny</option>
                </select>
              </label>
            </div>

            <div className="rounded border border-line bg-surface-2 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">Rules</span>
                <Button variant="ghost" type="button" onClick={addRule}>+ Add rule</Button>
              </div>
              {rules.length === 0 && <div className="text-xs text-ink-muted">No rules — only the default action applies.</div>}
              <div className="flex flex-col gap-2">
                {rules.map((r, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2">
                    <select value={r.action} onChange={(e) => setRule(i, { action: e.target.value as FwRule["action"] })} className={`${INPUT} py-1.5`}>
                      <option value="allow">allow</option>
                      <option value="deny">deny</option>
                    </select>
                    <select value={r.protocol} onChange={(e) => setRule(i, { protocol: e.target.value as FwRule["protocol"] })} className={`${INPUT} py-1.5`}>
                      <option value="any">any</option><option value="tcp">tcp</option><option value="udp">udp</option><option value="icmp">icmp</option>
                    </select>
                    <input value={r.src} onChange={(e) => setRule(i, { src: e.target.value })} placeholder="src" className={`${INPUT} w-28 py-1.5 font-mono text-xs`} />
                    <input value={r.dst} onChange={(e) => setRule(i, { dst: e.target.value })} placeholder="dst" className={`${INPUT} w-28 py-1.5 font-mono text-xs`} />
                    <input value={r.port} onChange={(e) => setRule(i, { port: e.target.value })} placeholder="port" className={`${INPUT} w-20 py-1.5 font-mono text-xs`} />
                    {shaping && (
                      <input inputMode="numeric" value={r.rate_kbps ?? ""} onChange={(e) => setRule(i, { rate_kbps: e.target.value ? Number(e.target.value.replace(/[^0-9]/g, "")) : null })}
                        placeholder="kbps" className={`${INPUT} w-24 py-1.5 font-mono text-xs`} title="Rate limit (kbps)" />
                    )}
                    <Button variant="ghost" type="button" onClick={() => rmRule(i)} aria-label="Remove rule">✕</Button>
                  </div>
                ))}
              </div>
            </div>

            {save.isError && <span className="text-xs text-critical">Could not save the policy.</span>}
            <div className="md-dialog-footer flex items-center justify-between gap-2">
              {editing ? (
                <RoleGate min="org_admin" mode="hide">
                  <Button variant="danger" type="button" disabled={del.isPending}
                    onClick={() => del.mutate(obj!.id, { onSuccess: onClose })}>
                    {del.isPending ? "…" : "Delete"}
                  </Button>
                </RoleGate>
              ) : <span />}
              <div className="flex items-center gap-2">
                <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
